import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { DataGrid } from '@mui/x-data-grid';
import dayjs from '../../../config/dayjs';

function getMethodChipColor(method) {
  const m = String(method || '').toUpperCase();
  switch (m) {
    case 'GET':
      return { bg: '#e0f2fe', color: '#0369a1', border: '#bae6fd' };
    case 'POST':
      return { bg: '#dcfce7', color: '#15803d', border: '#bbf7d0' };
    case 'PUT':
    case 'PATCH':
      return { bg: '#fef3c7', color: '#b45309', border: '#fde68a' };
    case 'DELETE':
      return { bg: '#fee2e2', color: '#b91c1c', border: '#fecaca' };
    default:
      return { bg: '#f3f4f6', color: '#4b5563', border: '#e5e7eb' };
  }
}

function parseNestedErrorMessage(rawMsg) {
  if (!rawMsg) return { text: '', details: null, prefix: '' };
  if (typeof rawMsg !== 'string') {
    if (typeof rawMsg === 'object') {
      return { text: JSON.stringify(rawMsg), details: rawMsg, prefix: '' };
    }
    return { text: String(rawMsg), details: null, prefix: '' };
  }

  const trimmed = rawMsg.trim();
  const braceIndex = trimmed.indexOf('{');
  const bracketIndex = trimmed.indexOf('[');
  let jsonStartIndex = -1;

  if (braceIndex !== -1 && bracketIndex !== -1) {
    jsonStartIndex = Math.min(braceIndex, bracketIndex);
  } else if (braceIndex !== -1) {
    jsonStartIndex = braceIndex;
  } else if (bracketIndex !== -1) {
    jsonStartIndex = bracketIndex;
  }

  if (jsonStartIndex !== -1) {
    const prefix = trimmed.slice(0, jsonStartIndex).trim().replace(/:$/, '').trim();
    const jsonStr = trimmed.slice(jsonStartIndex).trim();
    try {
      const parsed = JSON.parse(jsonStr);
      if (parsed && typeof parsed === 'object') {
        let textParts = [];
        if (Array.isArray(parsed)) {
          textParts = parsed.map((item) => (typeof item === 'object' ? JSON.stringify(item) : String(item)));
        } else {
          textParts = Object.entries(parsed).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`);
        }
        const text = (prefix ? `${prefix}: ` : '') + textParts.join(' | ');
        return { text, details: parsed, prefix };
      }
    } catch {
      // JSON parse failed, keep raw string
    }
  }

  return { text: trimmed, details: null, prefix: '' };
}

function renderStatusBadge(responseBody, row, onOpenPayload, defaultTab = 1) {
  const isFailedCol = defaultTab === 3;
  const rb = isFailedCol ? (row?.responseBodyFailed ?? responseBody ?? {}) : (responseBody ?? row?.responseBody ?? {});
  const statusCode =
    rb?.statusCode ??
    rb?.status_code ??
    rb?.code ??
    rb?.status ??
    (isFailedCol ? (row?.statusCodeFailed ?? row?.statusFailed) : (row?.statusCode ?? row?.status));
  const rawErrorMsg = rb?.message || rb?.error || '';
  const statusMsg = rb?.statusMessage || rb?.status_message || '';
  const isError = isFailedCol || (statusCode ? Number(statusCode) >= 400 : false);

  const { text: formattedErrorMsg, details: errorDetails, prefix: errorPrefix } = parseNestedErrorMessage(rawErrorMsg);

  const displayMsg = isError ? formattedErrorMsg || statusMsg : statusMsg || formattedErrorMsg;

  if (statusCode == null && !displayMsg) {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ pl: 0.5 }}>
        -
      </Typography>
    );
  }

  const isSuccess = !isError && (statusCode ? Number(statusCode) >= 200 && Number(statusCode) < 300 : true);

  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ maxWidth: '100%', height: '100%' }}>
      {statusCode != null && (
        <Chip
          label={statusCode}
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: 11,
            height: 22,
            bgcolor: isSuccess ? '#dcfce7' : '#fee2e2',
            color: isSuccess ? '#15803d' : '#b91c1c',
            border: `1px solid ${isSuccess ? '#bbf7d0' : '#fecaca'}`,
            flexShrink: 0
          }}
        />
      )}
      {displayMsg && (
        <Tooltip
          title={
            errorDetails ? (
              <Box sx={{ p: 0.5, maxWidth: 360 }}>
                {statusMsg && (
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5, color: '#f8fafc' }}>
                    {statusMsg}
                  </Typography>
                )}
                {errorPrefix && (
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5, color: '#fca5a5' }}>
                    {errorPrefix.toUpperCase()}
                  </Typography>
                )}
                {Array.isArray(errorDetails)
                  ? errorDetails.map((item, idx) => (
                      <Typography key={idx} variant="caption" sx={{ display: 'block', mb: 0.25, fontSize: 11.5 }}>
                        • {typeof item === 'object' ? JSON.stringify(item) : item}
                      </Typography>
                    ))
                  : Object.entries(errorDetails).map(([key, val], idx) => (
                      <Box key={idx} sx={{ mb: 0.5, fontSize: 11.5 }}>
                        <span style={{ fontWeight: 700, color: '#fde047' }}>{key}: </span>
                        <span style={{ color: '#f1f5f9' }}>{typeof val === 'object' ? JSON.stringify(val) : val}</span>
                      </Box>
                    ))}
              </Box>
            ) : (
              displayMsg
            )
          }
          arrow
        >
          <Typography
            variant="caption"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontWeight: isError ? 600 : 400,
              color: isError ? '#b91c1c' : 'text.secondary',
              maxWidth: 240,
              fontSize: 12
            }}
          >
            {displayMsg}
          </Typography>
        </Tooltip>
      )}
      {rb && typeof rb === 'object' && Object.keys(rb).length > 0 && onOpenPayload && (
        <Tooltip title="View Response Body" arrow>
          <IconButton size="small" onClick={() => onOpenPayload(row, defaultTab)} sx={{ p: 0.25, color: 'text.secondary', flexShrink: 0 }}>
            <CodeIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

function PayloadViewerDialog({ open, onClose, payloadData, initialTab = 0 }) {
  const [tab, setTab] = useState(initialTab);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setTab(initialTab);
  }, [initialTab, payloadData]);

  const reqBody = payloadData?.requestBody || payloadData?.request_body;
  const resBody = payloadData?.responseBody || payloadData?.response_body;
  const reqBodyFailed = payloadData?.requestBodyFailed || payloadData?.request_body_failed;
  const resBodyFailed = payloadData?.responseBodyFailed || payloadData?.response_body_failed;

  const getContent = () => {
    switch (tab) {
      case 0:
        return reqBody;
      case 1:
        return resBody;
      case 2:
        return reqBodyFailed;
      case 3:
        return resBodyFailed;
      default:
        return reqBody;
    }
  };

  const currentContent = getContent();

  const { details: currentErrorDetails, prefix: currentErrorPrefix } = useMemo(() => {
    if (currentContent && typeof currentContent === 'object') {
      const msg = currentContent.message || currentContent.error;
      if (typeof msg === 'string') {
        return parseNestedErrorMessage(msg);
      }
    } else if (typeof currentContent === 'string') {
      return parseNestedErrorMessage(currentContent);
    }
    return { details: null, prefix: '' };
  }, [currentContent]);

  const formattedContent = useMemo(() => {
    if (!currentContent || typeof currentContent !== 'object') return currentContent;
    if (currentErrorDetails) {
      return {
        ...currentContent,
        errorDetails: currentErrorDetails
      };
    }
    return currentContent;
  }, [currentContent, currentErrorDetails]);

  if (!payloadData) return null;

  const jsonString = formattedContent ? JSON.stringify(formattedContent, null, 2) : '(Empty)';

  const handleCopy = () => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={payloadData.method || 'GET'}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: 11,
                ...getMethodChipColor(payloadData.method)
              }}
            />
            <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
              {payloadData.endpoint || payloadData.path || payloadData.url}
            </Typography>
          </Stack>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs value={tab} onChange={(e, val) => setTab(val)} variant="scrollable" scrollButtons="auto">
          <Tab label={`Request Body ${reqBody ? '' : '(Empty)'}`} sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label={`Response Body ${resBody ? '' : '(Empty)'}`} sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label={`Request Body Failed ${reqBodyFailed ? '' : '(Empty)'}`} sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label={`Response Body Failed ${resBodyFailed ? '' : '(Empty)'}`} sx={{ textTransform: 'none', fontWeight: 600 }} />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 2, bgcolor: '#0f172a' }}>
        {currentErrorDetails && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              bgcolor: 'rgba(239, 68, 68, 0.12)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              borderRadius: 2
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#f87171', mb: 1, textTransform: 'capitalize' }}>
              {currentErrorPrefix ? `${currentErrorPrefix} Details:` : 'Error Details:'}
            </Typography>
            <Stack spacing={0.75}>
              {Array.isArray(currentErrorDetails)
                ? currentErrorDetails.map((err, i) => (
                    <Typography key={i} variant="caption" sx={{ color: '#fca5a5', fontFamily: 'monospace', fontSize: 12 }}>
                      • {typeof err === 'object' ? JSON.stringify(err) : err}
                    </Typography>
                  ))
                : Object.entries(currentErrorDetails).map(([k, v], i) => (
                    <Box key={i} sx={{ fontSize: 12.5, fontFamily: 'monospace' }}>
                      <span style={{ fontWeight: 700, color: '#fde047' }}>{k}: </span>
                      <span style={{ color: '#f1f5f9' }}>{typeof v === 'object' ? JSON.stringify(v) : v}</span>
                    </Box>
                  ))}
            </Stack>
          </Box>
        )}

        <Box sx={{ position: 'relative' }}>
          <Button
            size="small"
            startIcon={copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            onClick={handleCopy}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(255,255,255,0.1)',
              color: copied ? '#4ade80' : '#e2e8f0',
              textTransform: 'none',
              fontSize: 12,
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.2)'
              }
            }}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>

          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              color: '#38bdf8',
              fontFamily: 'monospace',
              fontSize: 12.5,
              lineHeight: 1.6,
              overflowX: 'auto',
              maxHeight: 400
            }}
          >
            {jsonString}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 1.5, px: 2 }}>
        <Button onClick={onClose} variant="outlined" size="small" sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function UserActivityDetailDialog({ open, onClose, user, searchDetail, setSearchDetail, filteredEndpointHistory }) {
  const layoutFormatDate = 'DD MMM YYYY HH:mm:ss';
  const [selectedPayload, setSelectedPayload] = useState(null); // { row, tab }

  const handleOpenPayload = (row, defaultTab = 0) => {
    setSelectedPayload({ row, tab: defaultTab });
  };

  const columns = useMemo(
    () => [
      {
        field: 'method',
        headerName: 'Method',
        width: 95,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const m = params.row?.method || params.value || 'GET';
          const styling = getMethodChipColor(m);
          return (
            <Chip
              label={m}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: 11,
                height: 22,
                bgcolor: styling.bg,
                color: styling.color,
                border: `1px solid ${styling.border}`
              }}
            />
          );
        }
      },
      {
        field: 'baseUrl',
        headerName: 'Base URL',
        width: 140,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const r = params.row || {};
          const val = r.baseUrl || r.base_url || params.value || '-';
          return (
            <Typography variant="body2" sx={{ fontSize: 12.5, color: val === '-' ? 'text.disabled' : 'text.secondary' }} noWrap title={val}>
              {val}
            </Typography>
          );
        }
      },
      {
        field: 'endpoint',
        headerName: 'Endpoint',
        minWidth: 220,
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => {
          const r = params.row || {};
          const val = r.endpoint || r.path || r.url || params.value || '-';
          return (
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12.5 }} noWrap title={val}>
              {val}
            </Typography>
          );
        }
      },
      {
        field: 'requestBody',
        headerName: 'Request Body',
        width: 130,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const reqBody = params.row?.requestBody || params.row?.request_body;
          if (!reqBody || (typeof reqBody === 'object' && Object.keys(reqBody).length === 0)) {
            return (
              <Typography variant="body2" color="text.disabled">
                -
              </Typography>
            );
          }
          return (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<CodeIcon sx={{ fontSize: 14 }} />}
              onClick={() => handleOpenPayload(params.row, 0)}
              sx={{
                textTransform: 'none',
                fontSize: 11,
                fontWeight: 600,
                py: 0.25,
                px: 1,
                borderRadius: 1.5,
                borderColor: '#cbd5e1',
                color: '#334155',
                bgcolor: '#f8fafc',
                '&:hover': {
                  bgcolor: '#f1f5f9',
                  borderColor: '#94a3b8'
                }
              }}
            >
              View Body
            </Button>
          );
        }
      },
      {
        field: 'requestBodyFailed',
        headerName: 'Request Body Failed',
        width: 155,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const reqBody = params.row?.requestBodyFailed;
          if (!reqBody || (typeof reqBody === 'object' && Object.keys(reqBody).length === 0)) {
            return (
              <Typography variant="body2" color="text.disabled">
                -
              </Typography>
            );
          }
          return (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<CodeIcon sx={{ fontSize: 14 }} />}
              onClick={() => handleOpenPayload(params.row, 2)}
              sx={{
                textTransform: 'none',
                fontSize: 11,
                fontWeight: 600,
                py: 0.25,
                px: 1,
                borderRadius: 1.5,
                borderColor: '#cbd5e1',
                color: '#334155',
                bgcolor: '#f8fafc',
                '&:hover': {
                  bgcolor: '#f1f5f9',
                  borderColor: '#94a3b8'
                }
              }}
            >
              View Body
            </Button>
          );
        }
      },
      {
        field: 'duration',
        headerName: 'Duration',
        width: 105,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const r = params.row || {};
          const val = r.duration || '-';
          return (
            <Typography variant="body2" sx={{ fontSize: 12.5, fontFamily: 'monospace' }} noWrap>
              {val}
            </Typography>
          );
        }
      },
      {
        field: 'lagDuration',
        headerName: 'Lag Duration',
        width: 115,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const r = params.row || {};
          const val = r.lagDuration || '-';
          return (
            <Typography variant="body2" sx={{ fontSize: 12.5, fontFamily: 'monospace' }} noWrap>
              {val}
            </Typography>
          );
        }
      },
      {
        field: 'totalHits',
        headerName: 'Total Hits',
        width: 95,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const r = params.row || {};
          const val = r.totalHits ?? r.total_hits ?? r.hits ?? r.count ?? params.value ?? 0;
          return (
            <Chip
              label={val}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: 11,
                height: 22,
                minWidth: 30,
                bgcolor: '#f1f5f9',
                color: '#334155'
              }}
            />
          );
        }
      },
      {
        field: 'successCount',
        headerName: 'Success',
        width: 90,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const r = params.row || {};
          const val = r.successCount ?? r.success_count ?? r.success ?? params.value ?? 0;
          return (
            <Chip
              label={val}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: 11,
                height: 22,
                minWidth: 30,
                bgcolor: '#dcfce7',
                color: '#15803d',
                border: '1px solid #bbf7d0'
              }}
            />
          );
        }
      },
      {
        field: 'failCount',
        headerName: 'Fail',
        width: 85,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const r = params.row || {};
          const val = r.failCount ?? r.fail_count ?? r.fail ?? params.value ?? 0;
          if (val > 0) {
            return (
              <Chip
                label={val}
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: 11,
                  height: 22,
                  minWidth: 30,
                  bgcolor: '#fee2e2',
                  color: '#b91c1c',
                  border: '1px solid #fecaca'
                }}
              />
            );
          }
          return (
            <Chip
              label={0}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: 11,
                height: 22,
                minWidth: 30,
                bgcolor: '#f8fafc',
                color: 'text.disabled',
                border: '1px solid #e2e8f0'
              }}
            />
          );
        }
      },
      {
        field: 'lastHitsAt',
        headerName: 'Last Hits At',
        width: 180,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const r = params.row || {};
          const val = r.lastHitsAt || r.last_hits_at || r.timestamp || r.last_hit || params.value;
          return (
            <Typography variant="body2" sx={{ fontSize: 12.5, color: val ? 'text.secondary' : 'text.disabled' }} noWrap>
              {val ? dayjs(val).format(layoutFormatDate) : '-'}
            </Typography>
          );
        }
      },
      {
        field: 'responseBody',
        headerName: 'Response Status & Message',
        minWidth: 240,
        flex: 1.2,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => renderStatusBadge(params.value, params.row, handleOpenPayload, 1)
      },
      {
        field: 'responseBodyFailed',
        headerName: 'Response Status & Message Failed',
        minWidth: 240,
        flex: 1.2,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => renderStatusBadge(params.value, params.row, handleOpenPayload, 3)
      }
    ],
    []
  );

  if (!user) return null;

  const userAgentsList = Array.isArray(user.userAgents || user.user_agents)
    ? user.userAgents || user.user_agents
    : user.userAgents || user.user_agents
      ? [user.userAgents || user.user_agents]
      : [];

  const usernameVal = user.username || user.user_name || user.name || '-';
  const companyVal = user.company || user.company_name || user.companyName || '-';
  const roleVal = user.role || user.role_name || user.roleName || '-';
  const ipVal = user.ip || user.ip_address || user.ipAddress || '-';
  const durationVal = user.durationHms || user.duration_hms || user.duration || '-';
  const lastLoginVal = user.lastLoginAt || user.last_login_at || user.lastLogin || null;
  const lastAccessVal = user.lastAccessEndpointAt || user.last_access_endpoint_at || user.lastAccess || null;
  const loginCountVal = user.loginCount ?? user.login_count ?? 0;
  const endpointsList = user.endpointHistory || user.endpoint_history || [];
  const accessEndpointCountVal =
    user.accessEndpointCount ?? user.access_endpoint_count ?? (Array.isArray(endpointsList) ? endpointsList.length : 0);
  const podIdVal = user.podId ?? user.pod_id ?? user.pod ?? null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper">
        <DialogTitle sx={{ m: 0, p: 2.5, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              User Activity Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {usernameVal} {companyVal !== '-' ? `(${companyVal})` : ''} — Role: {roleVal}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 1, bgcolor: '#f8fafc' }}>
          <Stack spacing={2.5}>
            {/* USER INFO SUMMARY CARD */}
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Username
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {usernameVal}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Company
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {companyVal}
                    </Typography>
                  </Grid>

                  {podIdVal != null && (
                    <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Pod ID
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="primary.main">
                        {podIdVal}
                      </Typography>
                    </Grid>
                  )}

                  <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Role
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {roleVal}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      IP Address
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {ipVal}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Duration (HMS)
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="primary.main">
                      {durationVal}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Last Login At
                    </Typography>
                    <Typography variant="body2">{lastLoginVal ? dayjs(lastLoginVal).format('DD MMM YYYY HH:mm') : '-'}</Typography>
                  </Grid>

                  <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Last Access Endpoint At
                    </Typography>
                    <Typography variant="body2">{lastAccessVal ? dayjs(lastAccessVal).format('DD MMM YYYY HH:mm:ss') : '-'}</Typography>
                  </Grid>

                  <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Login Count
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {loginCountVal}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Access Endpoint Count
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="info.main">
                      {accessEndpointCountVal}
                    </Typography>
                  </Grid>

                  {/* USER AGENTS */}
                  {userAgentsList.length > 0 && (
                    <Grid size={{ xs: 12 }}>
                      <Divider sx={{ my: 0.5 }} />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        User Agents:
                      </Typography>
                      <Stack spacing={0.5}>
                        {userAgentsList.map((ua, index) => (
                          <Typography
                            key={index}
                            variant="caption"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: 11,
                              bgcolor: '#f1f5f9',
                              p: 0.5,
                              px: 1,
                              borderRadius: 1,
                              wordBreak: 'break-all'
                            }}
                          >
                            {ua}
                          </Typography>
                        ))}
                      </Stack>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* ENDPOINT HISTORY SECTION */}
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ sm: 'center' }}
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Endpoint Access History
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total: {filteredEndpointHistory.length} endpoints
                    </Typography>
                  </Box>

                  {/* CLIENT-SIDE SEARCH */}
                  <TextField
                    size="small"
                    placeholder="Filter endpoint or method..."
                    value={searchDetail}
                    onChange={(e) => setSearchDetail(e.target.value)}
                    sx={{ width: { xs: '100%', sm: 300 } }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                        )
                      }
                    }}
                  />
                </Stack>

                <Box sx={{ height: 450, width: '100%' }}>
                  <DataGrid
                    rows={filteredEndpointHistory}
                    columns={columns}
                    getRowId={(row, index) =>
                      row.id ||
                      `${row.method || 'GET'}-${row.endpoint || row.path || row.url || 'ep'}-${row.lastHitsAt || row.last_hits_at || index}`
                    }
                    pageSizeOptions={[10, 25, 50, 100]}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 25,
                          page: 0
                        }
                      }
                    }}
                    density="compact"
                    rowHeight={48}
                    columnHeaderHeight={48}
                    headerHeight={48}
                    disableRowSelectionOnClick
                    sx={{
                      border: 'none',
                      '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#f8fafc',
                        color: '#334155',
                        fontWeight: 700,
                        fontSize: 12.5,
                        borderBottom: '1px solid #e2e8f0'
                      },
                      '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 700
                      },
                      '& .MuiDataGrid-cell': {
                        borderColor: '#f1f5f9',
                        fontSize: 12.5,
                        display: 'flex',
                        alignItems: 'center'
                      },
                      '& .MuiDataGrid-row:hover': {
                        backgroundColor: '#f8fafc'
                      }
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* PAYLOAD VIEWER SUB-DIALOG */}
      <PayloadViewerDialog
        open={Boolean(selectedPayload)}
        onClose={() => setSelectedPayload(null)}
        payloadData={selectedPayload?.row}
        initialTab={selectedPayload?.tab ?? 0}
      />
    </>
  );
}
